import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import OneSignal from 'react-onesignal';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import CourseSearchInput from '../components/CourseSearchInput';
import BundleTagsManager from '../components/BundleTagsManager';
import { 
  ArrowLeft, LogOut, CreditCard, Mail, User, Calendar, 
  Shield, Target, Trophy, DollarSign, RefreshCw, Save,
  Camera, Loader2, Pencil, MapPin, Briefcase, Phone, Star, Clock, BarChart3, Bell, Crown, Flag, UserPlus, X, Check, Lock, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
  'Delaware', 'District of Columbia', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
  'Wisconsin', 'Wyoming'
];

export default function Profile() {
  const { user, loading, logout, checkAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const [handicapInput, setHandicapInput] = useState('');
  const [savingHandicap, setSavingHandicap] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [golferProfile, setGolferProfile] = useState({});
  const fileInputRef = useRef(null);
  // GHIN state
  const [ghinInput, setGhinInput] = useState('');
  const [ghinHandicapInput, setGhinHandicapInput] = useState('');
  const [ghinLinking, setGhinLinking] = useState(false);

  const [form, setForm] = useState({
    name: '', phone: '', city: '', state: '', zip_code: '',
    handicap: '', average_score: '', years_playing: '', home_course: '',
    favorite_courses: '', play_frequency: '', preferred_tee_time: '',
    profession: '', golf_goals: '',
  });
  const [clubStats, setClubStats] = useState(null);
  const [notifPrefs, setNotifPrefs] = useState(null);
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate('/');
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) {
      setHandicapInput(user.handicap_index?.toString() || '');
      fetchStats();
      fetchProfile();
      fetchClubStats();
      fetchNotifPrefs();
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/stats`, { credentials: 'include' });
      if (res.ok) setStats(await res.json());
    } catch {}
  };

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API}/user/golfer-profile`, { credentials: 'include' });
      if (res.ok) {
        const profile = await res.json();
        setGolferProfile(profile);
        setForm({
          name: user.name || '',
          phone: profile.phone || user.phone_number || '',
          city: profile.city || '',
          state: profile.state || '',
          zip_code: profile.zip_code || '',
          handicap: profile.handicap?.toString() || '',
          average_score: profile.average_score?.toString() || '',
          years_playing: profile.years_playing || '',
          home_course: profile.home_course || '',
          favorite_courses: profile.favorite_courses || '',
          play_frequency: profile.play_frequency || '',
          preferred_tee_time: profile.preferred_tee_time || '',
          profession: profile.profession || '',
          golf_goals: profile.golf_goals || '',
        });
      }
    } catch {}
  };

  const fetchClubStats = async () => {
    try {
      const res = await fetch(`${API}/shots/club-stats`, { credentials: 'include' });
      if (res.ok) setClubStats(await res.json());
    } catch {}
  };

  const fetchNotifPrefs = async () => {
    try {
      const res = await fetch(`${API}/user/notification-preferences`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setNotifPrefs(data);
      }
    } catch (err) {
      console.error('Error fetching notification preferences:', err);
    }
  };

  const toggleNotifPref = async (key, value) => {
    try {
      const newPrefs = { ...notifPrefs, [key]: value };
      setNotifPrefs(newPrefs);
      
      await fetch(`${API}/user/notification-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newPrefs)
      });
    } catch (err) {
      console.error('Error updating notification preference:', err);
      toast.error('Failed to update preference');
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current || !passwordForm.new || !passwordForm.confirm) {
      toast.error('Please fill in all fields');
      return;
    }
    if (passwordForm.new.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    
    setChangingPassword(true);
    try {
      const res = await fetch(`${API}/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: passwordForm.current,
          new_password: passwordForm.new
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Password changed successfully');
        setShowChangePassword(false);
        setPasswordForm({ current: '', new: '', confirm: '' });
      } else {
        toast.error(data.detail || 'Failed to change password');
      }
    } catch (err) {
      toast.error('Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo must be under 5MB'); return; }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await fetch(`${API}/upload/media`, { method: 'POST', body: formData, credentials: 'include' });
      if (!uploadRes.ok) { toast.error('Upload failed'); return; }
      const { media_url } = await uploadRes.json();

      const updateRes = await fetch(`${API}/user/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ picture: media_url }),
      });
      if (updateRes.ok) {
        toast.success('Profile photo updated!');
        checkAuth();
      }
    } catch { toast.error('Upload failed'); }
    finally { setUploadingPhoto(false); }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/user/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          city: form.city,
          state: form.state,
          zip_code: form.zip_code,
          handicap: form.handicap ? parseFloat(form.handicap) : null,
          average_score: form.average_score ? parseInt(form.average_score) : null,
          years_playing: form.years_playing,
          home_course: form.home_course,
          favorite_courses: form.favorite_courses,
          play_frequency: form.play_frequency,
          preferred_tee_time: form.preferred_tee_time,
          profession: form.profession,
          golf_goals: form.golf_goals,
        }),
      });
      if (res.ok) {
        toast.success('Profile updated!');
        setEditing(false);
        checkAuth();
        fetchProfile();
      } else {
        toast.error('Failed to save');
      }
    } catch { toast.error('Network error'); }
    finally { setSaving(false); }
  };

  const handleSaveHandicap = async () => {
    if (!handicapInput) { toast.error('Enter a handicap value'); return; }
    setSavingHandicap(true);
    try {
      const res = await fetch(`${API}/handicap`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ handicap_index: parseFloat(handicapInput) }),
      });
      if (res.ok) { toast.success('Handicap updated!'); checkAuth(); }
      else toast.error('Failed to update');
    } catch { toast.error('Error'); } finally { setSavingHandicap(false); }
  };

  const handleRecalculateHandicap = async () => {
    setRecalculating(true);
    try {
      const res = await fetch(`${API}/handicap/recalculate`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        const result = await res.json();
        if (result.handicap_index) {
          toast.success(`Handicap recalculated: ${result.handicap_index}`);
          setHandicapInput(result.handicap_index.toString());
          checkAuth();
        } else toast.info(result.message);
      }
    } catch { toast.error('Error'); } finally { setRecalculating(false); }
  };

  const handleGhinLookup = async () => {
    if (!ghinInput || ghinInput.length < 5) { toast.error('Enter a valid GHIN number (at least 5 digits)'); return; }
    if (!ghinHandicapInput) { toast.error('Enter your official GHIN handicap index'); return; }
    setGhinLinking(true);
    try {
      const res = await fetch(`${API}/ghin/link`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ ghin_number: ghinInput, ghin_handicap: parseFloat(ghinHandicapInput) }),
      });
      if (res.ok) {
        toast.success('GHIN account linked!');
        setGhinInput('');
        setGhinHandicapInput('');
        checkAuth();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to link');
      }
    } catch { toast.error('Network error'); } finally { setGhinLinking(false); }
  };

  const handleGhinLink = handleGhinLookup;

  const handleGhinUnlink = async () => {
    try {
      const res = await fetch(`${API}/ghin/unlink`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        toast.success('GHIN account unlinked');
        setGhinInput('');
        setGhinHandicapInput('');
        checkAuth();
      }
    } catch { toast.error('Error'); }
  };

  const handleGhinUpdateHandicap = async () => {
    if (!ghinHandicapInput) { toast.error('Enter updated handicap'); return; }
    setGhinLinking(true);
    try {
      const res = await fetch(`${API}/ghin/handicap`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ ghin_number: user.ghin_number, ghin_handicap: parseFloat(ghinHandicapInput) }),
      });
      if (res.ok) {
        toast.success('GHIN handicap updated!');
        setGhinHandicapInput('');
        checkAuth();
      }
    } catch { toast.error('Error'); } finally { setGhinLinking(false); }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green"></div>
      </div>
    );
  }

  const initials = user.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  const memberSince = user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A';
  const membershipExpires = user.membership_expires ? new Date(user.membership_expires).toLocaleDateString() : null;

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav md:pb-8">
      <Navigation user={user} onLogout={logout} />

      <div className="max-w-2xl mx-auto px-4 md:px-8 pt-20 md:pt-24">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-4 text-bundle-green hover:bg-bundle-cream-dark -ml-2">
          <ArrowLeft className="w-5 h-5 mr-2" /> Back
        </Button>

        {/* Profile Header with Photo Upload */}
        <div className="text-center mb-8">
          <div className="relative inline-block">
            <Avatar className="w-24 h-24 mx-auto border-4 border-white shadow-xl">
              <AvatarImage src={user.picture} alt={user.name} />
              <AvatarFallback className="bg-bundle-green text-white text-2xl font-heading">{initials}</AvatarFallback>
            </Avatar>
            <button
              data-testid="change-photo-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="absolute bottom-0 right-0 w-8 h-8 bg-bundle-gold rounded-full flex items-center justify-center shadow-lg hover:bg-bundle-gold-dark transition-colors border-2 border-white"
            >
              {uploadingPhoto ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Camera className="w-4 h-4 text-white" />}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
          </div>
          <h1 className="font-heading font-bold text-3xl text-bundle-green mt-4 mb-1">{user.name}</h1>
          <p className="font-body text-gray-600">{user.email}</p>
          {(golferProfile.city || golferProfile.state) && (
            <p className="font-body text-sm text-gray-500 flex items-center justify-center gap-1 mt-1">
              <MapPin className="w-3.5 h-3.5" /> {[golferProfile.city, golferProfile.state].filter(Boolean).join(', ')}
            </p>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="bg-white border-bundle-cream-dark">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 text-bundle-gold mx-auto mb-2" />
              <p className="font-heading text-2xl font-bold text-bundle-green">{user.handicap_index?.toFixed(1) || '--'}</p>
              <p className="font-body text-xs text-gray-500">App Handicap</p>
            </CardContent>
          </Card>
          <Card className={`border-bundle-cream-dark ${user.ghin_number ? 'bg-blue-50/50' : 'bg-white'}`}>
            <CardContent className="p-4 text-center">
              <Shield className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <p className="font-heading text-2xl font-bold text-bundle-green">{user.ghin_handicap?.toFixed(1) || '--'}</p>
              <p className="font-body text-xs text-gray-500">GHIN Official</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-bundle-cream-dark">
            <CardContent className="p-4 text-center">
              <Trophy className="w-6 h-6 text-bundle-gold mx-auto mb-2" />
              <p className="font-heading text-2xl font-bold text-bundle-green">{stats?.bundle_wins || 0}</p>
              <p className="font-body text-xs text-gray-500">Wins</p>
            </CardContent>
          </Card>
        </div>

        {/* Editable Profile Info */}
        <Card className="mb-6 bg-white border-bundle-cream-dark">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-bundle-gold" /> My Profile
            </CardTitle>
            {!editing ? (
              <Button data-testid="edit-profile-btn" variant="outline" size="sm" onClick={() => setEditing(true)} className="text-bundle-green border-bundle-green">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setEditing(false); fetchProfile(); }} className="text-gray-500">Cancel</Button>
                <Button data-testid="save-profile-btn" size="sm" onClick={handleSaveProfile} disabled={saving} className="btn-bundle">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {editing ? (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="font-body text-xs text-gray-500">Name</Label>
                    <Input data-testid="profile-name" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="mt-1" />
                  </div>
                  <div>
                    <Label className="font-body text-xs text-gray-500">Phone</Label>
                    <Input data-testid="profile-phone" value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} className="mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="font-body text-xs text-gray-500">City</Label>
                    <Input data-testid="profile-city" value={form.city} onChange={e => setForm(f => ({...f, city: e.target.value}))} className="mt-1" />
                  </div>
                  <div>
                    <Label className="font-body text-xs text-gray-500">State</Label>
                    <select data-testid="profile-state" value={form.state} onChange={e => setForm(f => ({...f, state: e.target.value}))} className="mt-1 w-full h-9 px-2 rounded-md border border-gray-200 text-sm font-body">
                      <option value="">Select</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="font-body text-xs text-gray-500">Zip</Label>
                    <Input data-testid="profile-zip" value={form.zip_code} onChange={e => setForm(f => ({...f, zip_code: e.target.value}))} className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label className="font-body text-xs text-gray-500">Profession</Label>
                  <Input data-testid="profile-profession" value={form.profession} onChange={e => setForm(f => ({...f, profession: e.target.value}))} className="mt-1" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="font-body text-xs text-gray-500">Handicap</Label>
                    <Input data-testid="profile-handicap" type="number" step="0.1" value={form.handicap} onChange={e => setForm(f => ({...f, handicap: e.target.value}))} className="mt-1" />
                  </div>
                  <div>
                    <Label className="font-body text-xs text-gray-500">Avg Score</Label>
                    <Input data-testid="profile-avg-score" type="number" value={form.average_score} onChange={e => setForm(f => ({...f, average_score: e.target.value}))} className="mt-1" />
                  </div>
                  <div>
                    <Label className="font-body text-xs text-gray-500">Years Playing</Label>
                    <select data-testid="profile-years" value={form.years_playing} onChange={e => setForm(f => ({...f, years_playing: e.target.value}))} className="mt-1 w-full h-9 px-2 rounded-md border border-gray-200 text-sm font-body">
                      <option value="">Select</option>
                      {['<1', '1-3', '3-10', '10+'].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="font-body text-xs text-gray-500">Home Course</Label>
                  <div className="mt-1">
                    <CourseSearchInput
                      value={form.home_course}
                      onChange={(val) => setForm(f => ({...f, home_course: val}))}
                      placeholder="Search your home course..."
                    />
                  </div>
                </div>
                <div>
                  <Label className="font-body text-xs text-gray-500">Favorite Courses</Label>
                  <Input data-testid="profile-fav-courses" value={form.favorite_courses} onChange={e => setForm(f => ({...f, favorite_courses: e.target.value}))} placeholder="Course 1, Course 2" className="mt-1" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="font-body text-xs text-gray-500">Play Frequency</Label>
                    <select data-testid="profile-frequency" value={form.play_frequency} onChange={e => setForm(f => ({...f, play_frequency: e.target.value}))} className="mt-1 w-full h-9 px-2 rounded-md border border-gray-200 text-sm font-body">
                      <option value="">Select</option>
                      {['1-2/month', 'weekly', '2-3/week', 'daily'].map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="font-body text-xs text-gray-500">Preferred Tee Time</Label>
                    <select data-testid="profile-tee-time" value={form.preferred_tee_time} onChange={e => setForm(f => ({...f, preferred_tee_time: e.target.value}))} className="mt-1 w-full h-9 px-2 rounded-md border border-gray-200 text-sm font-body">
                      <option value="">Select</option>
                      {['early morning', 'morning', 'midday', 'afternoon', 'twilight'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <Label className="font-body text-xs text-gray-500">Golf Goals</Label>
                  <Input data-testid="profile-goals" value={form.golf_goals} onChange={e => setForm(f => ({...f, golf_goals: e.target.value}))} placeholder="Break 80, play in tournaments..." className="mt-1" />
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <InfoRow icon={<User className="w-4 h-4 text-bundle-gold" />} label="Name" value={user.name} />
                <InfoRow icon={<Mail className="w-4 h-4 text-bundle-gold" />} label="Email" value={user.email} />
                <InfoRow icon={<Phone className="w-4 h-4 text-bundle-gold" />} label="Phone" value={golferProfile.phone || user.phone_number || '-'} />
                <InfoRow icon={<MapPin className="w-4 h-4 text-bundle-gold" />} label="Location" value={[golferProfile.city, golferProfile.state, golferProfile.zip_code].filter(Boolean).join(', ') || '-'} />
                <InfoRow icon={<Briefcase className="w-4 h-4 text-bundle-gold" />} label="Profession" value={golferProfile.profession || '-'} />
                <InfoRow icon={<Star className="w-4 h-4 text-bundle-gold" />} label="Home Course" value={golferProfile.home_course || '-'} />
                <InfoRow icon={<Star className="w-4 h-4 text-bundle-gold" />} label="Favorite Courses" value={golferProfile.favorite_courses || '-'} />
                <InfoRow icon={<Target className="w-4 h-4 text-bundle-gold" />} label="Handicap / Avg Score" value={`${golferProfile.handicap ?? '-'} / ${golferProfile.average_score ?? '-'}`} />
                <InfoRow icon={<Calendar className="w-4 h-4 text-bundle-gold" />} label="Years Playing" value={golferProfile.years_playing || '-'} />
                <InfoRow icon={<Clock className="w-4 h-4 text-bundle-gold" />} label="Play Frequency" value={golferProfile.play_frequency || '-'} />
                <InfoRow icon={<Clock className="w-4 h-4 text-bundle-gold" />} label="Preferred Tee Time" value={golferProfile.preferred_tee_time || '-'} />
                <InfoRow icon={<Target className="w-4 h-4 text-bundle-gold" />} label="Golf Goals" value={golferProfile.golf_goals || '-'} />
                <InfoRow icon={<Calendar className="w-4 h-4 text-bundle-gold" />} label="Member Since" value={memberSince} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bundle Tags — discoverability */}
        <BundleTagsCard initial={user.tags || []} onSaved={() => checkAuth && checkAuth()} />

        {/* Clear cache & reload — belt-and-suspenders for stuck installed PWAs */}
        <Card className="mb-6 bg-white border-bundle-cream-dark">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-bundle-green">Troubleshooting</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-3">
              If the app is behaving oddly or showing outdated content, clear the local cache and reload to fetch the latest version.
            </p>
            <Button
              type="button"
              variant="outline"
              data-testid="clear-cache-reload-btn"
              onClick={async () => {
                try {
                  const cacheKeys = await caches.keys();
                  await Promise.all(cacheKeys.map((k) => caches.delete(k)));
                  const regs = await navigator.serviceWorker.getRegistrations();
                  await Promise.all(regs.map((r) => r.unregister()));
                } catch (e) {
                  console.warn('Clear-cache: partial failure, reloading anyway.', e);
                }
                window.location.reload();
              }}
              className="border-bundle-green text-bundle-green hover:bg-bundle-green hover:text-white"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Clear cache &amp; reload
            </Button>
          </CardContent>
        </Card>

        {/* Membership Status */}
        <Card className={`mb-6 ${user.is_member ? 'border-bundle-gold bg-bundle-gold/5' : 'border-bundle-cream-dark bg-white'}`}>
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Shield className={`w-5 h-5 ${user.is_member ? 'text-bundle-gold' : 'text-gray-400'}`} /> Membership Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {user.is_member ? (
              <div>
                <p className="font-body text-bundle-green font-semibold mb-1">Active Member</p>
                <p className="font-body text-sm text-gray-600">Expires: {membershipExpires}</p>
              </div>
            ) : (
              <div>
                <p className="font-body text-gray-600 mb-3">Upgrade to unlock all features</p>
                <Button data-testid="upgrade-btn" onClick={() => navigate('/membership')} className="btn-bundle">
                  <CreditCard className="w-4 h-4 mr-2" /> Start Free Trial
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Handicap Management */}
        <Card className="mb-6 bg-white border-bundle-cream-dark">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-bundle-gold" /> USGA Handicap
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <Input type="number" step="0.1" placeholder="Enter handicap index" value={handicapInput} onChange={e => setHandicapInput(e.target.value)} className="flex-1 border-bundle-cream-dark" />
              <Button onClick={handleSaveHandicap} disabled={savingHandicap} className="btn-bundle">
                {savingHandicap ? <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white" /> : <Save className="w-4 h-4" />}
              </Button>
            </div>
            <Button onClick={handleRecalculateHandicap} disabled={recalculating} variant="outline" className="w-full border-bundle-green text-bundle-green hover:bg-bundle-green/5">
              {recalculating ? <><div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-bundle-green mr-2" /> Calculating...</> : <><RefreshCw className="w-4 h-4 mr-2" /> Recalculate from Rounds</>}
            </Button>
            <p className="font-body text-xs text-gray-500">Auto-calculation requires at least 3 rounds with course rating and slope data</p>
          </CardContent>
        </Card>

        {/* My Rounds — quick access to round history */}
        <Card className="mb-6 bg-white border-bundle-cream-dark" data-testid="my-rounds-card">
          <CardContent className="p-4">
            <button
              onClick={() => navigate('/rounds')}
              className="w-full flex items-center gap-4 p-2 hover:bg-bundle-cream/40 rounded-lg transition-colors text-left"
              data-testid="open-my-rounds-btn"
            >
              <div className="w-12 h-12 rounded-full bg-bundle-green/10 flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-bundle-green" />
              </div>
              <div className="flex-1">
                <p className="font-heading font-bold text-bundle-green text-base">My Rounds</p>
                <p className="font-body text-sm text-gray-500">Every round you've posted, scanned, or played live</p>
              </div>
              <ArrowLeft className="w-5 h-5 text-gray-400 rotate-180" />
            </button>
          </CardContent>
        </Card>

        {/* GHIN Account Link */}
        <Card className="mb-6 bg-white border-bundle-cream-dark" data-testid="ghin-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" /> GHIN Account
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {user.ghin_number ? (
              <>
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-body text-xs text-gray-500 uppercase tracking-wider">GHIN Number</p>
                      <p className="font-heading text-lg font-bold text-bundle-green" data-testid="ghin-number-display">{user.ghin_number}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-body text-xs text-gray-500 uppercase tracking-wider">Official Handicap</p>
                      <p className="font-heading text-2xl font-bold text-blue-600" data-testid="ghin-handicap-display">{user.ghin_handicap?.toFixed(1) ?? '--'}</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input
                    data-testid="ghin-update-handicap-input"
                    type="number"
                    step="0.1"
                    placeholder="Update handicap index"
                    value={ghinHandicapInput}
                    onChange={e => setGhinHandicapInput(e.target.value)}
                    className="flex-1 border-bundle-cream-dark"
                  />
                  <Button data-testid="ghin-update-btn" onClick={handleGhinUpdateHandicap} disabled={ghinLinking} className="bg-blue-600 hover:bg-blue-700 text-white">
                    {ghinLinking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                </div>
                <Button data-testid="ghin-unlink-btn" onClick={handleGhinUnlink} variant="outline" size="sm" className="border-red-200 text-red-500 hover:bg-red-50">
                  <X className="w-4 h-4 mr-1" /> Unlink GHIN Account
                </Button>
              </>
            ) : (
              <>
                <p className="font-body text-sm text-gray-600">
                  Link your GHIN account to display your official USGA handicap index alongside your app-calculated handicap.
                </p>
                <div className="space-y-3">
                  <div>
                    <Label className="font-body text-xs text-gray-500">GHIN Number</Label>
                    <Input
                      data-testid="ghin-input"
                      type="text"
                      placeholder="Enter your GHIN number"
                      value={ghinInput}
                      onChange={e => setGhinInput(e.target.value.replace(/\D/g, ''))}
                      className="mt-1 border-bundle-cream-dark"
                    />
                  </div>
                  <div>
                    <Label className="font-body text-xs text-gray-500">Official Handicap Index</Label>
                    <Input
                      data-testid="ghin-handicap-input"
                      type="number"
                      step="0.1"
                      placeholder="Enter your GHIN handicap (e.g. 12.4)"
                      value={ghinHandicapInput}
                      onChange={e => setGhinHandicapInput(e.target.value)}
                      className="mt-1 border-bundle-cream-dark"
                    />
                  </div>
                  <Button data-testid="ghin-link-btn" onClick={handleGhinLink} disabled={ghinLinking} className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                    {ghinLinking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                    Link GHIN Account
                  </Button>
                </div>
                <p className="font-body text-xs text-gray-400">
                  Find your GHIN number at <a href="https://www.ghin.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">ghin.com</a>
                </p>
              </>
            )}
          </CardContent>
        </Card>

        {/* Golf Performance Stats */}
        {clubStats && Object.keys(clubStats.clubs || {}).length > 0 && (
          <Card className="mb-6 bg-white border-bundle-cream-dark" data-testid="club-stats-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-bundle-gold" /> Golf Performance Stats
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-bundle-cream-dark">
                      <th className="text-left py-2 font-heading text-xs text-gray-500 uppercase">Club</th>
                      <th className="text-center py-2 font-heading text-xs text-gray-500 uppercase">Avg Dist</th>
                      <th className="text-center py-2 font-heading text-xs text-gray-500 uppercase">Longest</th>
                      <th className="text-center py-2 font-heading text-xs text-gray-500 uppercase">Dispersion</th>
                      <th className="text-center py-2 font-heading text-xs text-gray-500 uppercase">Shots</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(clubStats.clubs)
                      .sort((a, b) => b[1].average_distance - a[1].average_distance)
                      .map(([club, data]) => (
                        <tr key={club} className="border-b border-bundle-cream-dark last:border-b-0">
                          <td className="py-2 font-body font-medium text-bundle-green">{club}</td>
                          <td className="py-2 text-center font-heading font-bold text-bundle-green">{data.average_distance} yds</td>
                          <td className="py-2 text-center font-body text-gray-600">{data.longest} yds</td>
                          <td className="py-2 text-center font-body text-gray-500">{data.dispersion > 0 ? `${data.dispersion} yds` : '-'}</td>
                          <td className="py-2 text-center font-body text-gray-400">{data.shot_count}</td>
                        </tr>
                      ))
                    }
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* No Club Stats Message */}
        {(!clubStats || Object.keys(clubStats.clubs || {}).length === 0) && (
          <Card className="mb-6 bg-white border-bundle-cream-dark">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-bundle-gold" /> Golf Performance Stats
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-body text-sm text-gray-500 text-center py-4">
                Start tracking your clubs during live rounds to see average distances, longest shots, and shot dispersion stats here.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Notification Preferences */}
        <Card className="mb-6 bg-white border-bundle-cream-dark" data-testid="notif-prefs-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Bell className="w-5 h-5 text-bundle-gold" /> Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Push Notifications - Real browser permission state */}
            <PushNotificationToggle />

            {/* Tournament Notification Preferences */}
            <NotificationToggle
              label="New Tournaments"
              description="Get notified when new events are added"
              prefKey="new_tournaments"
              notifPrefs={notifPrefs}
              setNotifPrefs={setNotifPrefs}
            />
            <NotificationToggle
              label="Tournament Reminders"
              description="24-hour and 1-hour tee time reminders"
              prefKey="tournament_reminders"
              notifPrefs={notifPrefs}
              setNotifPrefs={setNotifPrefs}
            />
            <NotificationToggle
              label="Tee Time Updates"
              description="When you're assigned a tee time"
              prefKey="tee_time_updates"
              notifPrefs={notifPrefs}
              setNotifPrefs={setNotifPrefs}
            />
            
            <p className="text-xs text-gray-400 px-3 pt-2">
              Install the app to your home screen for the best notification experience.
            </p>
          </CardContent>
        </Card>

        {/* Security - Change Password */}
        <Card className="mb-4 bg-white border-bundle-cream-dark">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Lock className="w-5 h-5 text-bundle-green" /> Security
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              data-testid="change-password-btn"
              onClick={() => setShowChangePassword(true)}
              variant="outline"
              className="w-full border-bundle-green text-bundle-green hover:bg-bundle-green/10"
            >
              <Lock className="w-4 h-4 mr-2" /> Change Password
            </Button>
          </CardContent>
        </Card>

        {/* Sign Out */}
        <Button data-testid="profile-logout-btn" onClick={async () => { await logout(); navigate('/'); }} variant="outline" className="w-full border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400 mb-6">
          <LogOut className="w-4 h-4 mr-2" /> Sign Out
        </Button>

        {/* Change Password Modal */}
        <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-bundle-green flex items-center gap-2">
                <Lock className="w-5 h-5" /> Change Password
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label className="text-sm font-body text-gray-600">Current Password</Label>
                <div className="relative mt-1">
                  <Input
                    data-testid="current-password-input"
                    type={showCurrentPw ? "text" : "password"}
                    value={passwordForm.current}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, current: e.target.value }))}
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPw(!showCurrentPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-body text-gray-600">New Password</Label>
                <div className="relative mt-1">
                  <Input
                    data-testid="new-password-input"
                    type={showNewPw ? "text" : "password"}
                    value={passwordForm.new}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, new: e.target.value }))}
                    placeholder="Enter new password (min 6 characters)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPw(!showNewPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-body text-gray-600">Confirm New Password</Label>
                <Input
                  data-testid="confirm-password-input"
                  type="password"
                  value={passwordForm.confirm}
                  onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
                  placeholder="Confirm new password"
                  className="mt-1"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowChangePassword(false);
                    setPasswordForm({ current: '', new: '', confirm: '' });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="submit-password-change"
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                  className="flex-1 btn-bundle"
                >
                  {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3 py-2 border-b border-bundle-cream-dark last:border-b-0">
      {icon}
      <div className="flex-1 min-w-0">
        <p className="font-body text-xs uppercase tracking-wide text-gray-500">{label}</p>
        <p className="font-body text-sm text-bundle-green truncate">{value}</p>
      </div>
    </div>
  );
}

function NotificationToggle({ label, description, prefKey, notifPrefs, setNotifPrefs }) {
  const isEnabled = notifPrefs?.[prefKey] !== false; // Default to true
  
  const toggle = async () => {
    const newValue = !isEnabled;
    const newPrefs = { ...notifPrefs, [prefKey]: newValue };
    setNotifPrefs(newPrefs);
    
    try {
      await fetch(`${window.location.origin}/api/user/notification-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newPrefs)
      });
    } catch (err) {
      console.error('Error updating notification preference:', err);
    }
  };
  
  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <div className="flex-1 min-w-0">
        <p className="font-body text-sm text-gray-700">{label}</p>
        <p className="font-body text-xs text-gray-500">{description}</p>
      </div>
      <button
        onClick={toggle}
        className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${
          isEnabled ? 'bg-bundle-green' : 'bg-gray-300'
        }`}
        data-testid={`toggle-${prefKey}`}
      >
        <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${
          isEnabled ? 'translate-x-5' : 'translate-x-0'
        }`} />
      </button>
    </div>
  );
}


const PROMPT_STORAGE_KEY = 'bundle_notif_prompt_v1';

function PushNotificationToggle() {
  const [permission, setPermission] = useState('unsupported');
  const [busy, setBusy] = useState(false);

  // Initial + tab-focus refresh
  useEffect(() => {
    const refresh = () => {
      if (typeof Notification === 'undefined') {
        setPermission('unsupported');
      } else {
        setPermission(Notification.permission); // 'default' | 'granted' | 'denied'
      }
    };
    refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      let granted = false;
      try {
        if (OneSignal?.Notifications?.requestPermission) {
          granted = await OneSignal.Notifications.requestPermission();
        } else {
          const res = await Notification.requestPermission();
          granted = res === 'granted';
        }
      } catch {
        const res = await Notification.requestPermission();
        granted = res === 'granted';
      }
      setPermission(Notification.permission);
      // Reset our snooze flag so future prompt logic respects browser state
      try { localStorage.removeItem(PROMPT_STORAGE_KEY); } catch {}
      if (granted) {
        toast.success('Notifications enabled — you\'re all set');
      } else if (Notification.permission === 'denied') {
        toast.error('Notifications blocked at the browser level. Enable them in your browser/site settings.');
      }
    } finally {
      setBusy(false);
    }
  };

  const disable = () => {
    // Browsers don't allow JS to revoke permission; instruct user.
    toast.info('To disable, turn off notifications in your browser/site settings.');
  };

  // ── Render variants ──
  if (permission === 'unsupported') {
    return (
      <div className="flex items-center gap-3 px-3 py-3 rounded-lg bg-gray-50 border border-gray-200" data-testid="push-toggle-unsupported">
        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
          <Bell className="w-4 h-4 text-gray-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-body text-sm font-semibold text-gray-600">Push Notifications</p>
          <p className="font-body text-xs text-gray-500">
            Not supported in this browser. Add the app to your home screen for push.
          </p>
        </div>
      </div>
    );
  }

  if (permission === 'granted') {
    return (
      <div className="flex items-center gap-3 px-3 py-3 rounded-lg bg-bundle-green/5 border border-bundle-green/10" data-testid="push-toggle-on">
        <div className="w-8 h-8 rounded-full bg-bundle-green/10 flex items-center justify-center flex-shrink-0">
          <Bell className="w-4 h-4 text-bundle-green" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-body text-sm font-semibold text-bundle-green">Push Notifications</p>
          <p className="font-body text-xs text-gray-500">
            On — you'll get game invites, leaderboard moves & skins payouts
          </p>
        </div>
        <button
          onClick={disable}
          className="w-11 h-6 rounded-full flex items-center px-0.5 bg-bundle-green flex-shrink-0"
          data-testid="push-toggle-disable"
          aria-label="Push notifications enabled"
        >
          <div className="w-5 h-5 bg-white rounded-full shadow translate-x-5" />
        </button>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="px-3 py-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="push-toggle-denied">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Bell className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-body text-sm font-semibold text-amber-800">Push Notifications Blocked</p>
            <p className="font-body text-xs text-amber-700">
              You denied notifications. Re-enable them in your browser's site settings, then refresh this page.
            </p>
          </div>
        </div>
        <p className="text-[11px] text-amber-700 ml-11">
          <strong>Chrome/Android:</strong> Tap the lock icon → Notifications → Allow.
          <br />
          <strong>iOS:</strong> Settings → Notifications → The Bundle.
        </p>
      </div>
    );
  }

  // permission === 'default' → off, can be enabled
  return (
    <div className="flex items-center gap-3 px-3 py-3 rounded-lg bg-bundle-gold/10 border border-bundle-gold/30" data-testid="push-toggle-off">
      <div className="w-8 h-8 rounded-full bg-bundle-gold/20 flex items-center justify-center flex-shrink-0">
        <Bell className="w-4 h-4 text-bundle-gold-dark" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-body text-sm font-semibold text-bundle-green">Push Notifications</p>
        <p className="font-body text-xs text-gray-600">
          Off — turn on to get pinged about games, payouts, and rank moves
        </p>
      </div>
      <Button
        onClick={enable}
        disabled={busy}
        size="sm"
        className="bg-bundle-gold text-bundle-green-dark hover:bg-bundle-gold-dark font-bold flex-shrink-0 text-xs"
        data-testid="push-toggle-enable"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Enable'}
      </Button>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Bundle Tags card — sits in the Profile page between My Profile and Membership.
// Edit mode toggles the selector; otherwise renders the read-only chip strip.
// ──────────────────────────────────────────────────────────────────────
function BundleTagsCard({ initial, onSaved }) {
  const [tags, setTags] = useState(initial || []);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setTags(initial || []); }, [initial]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/users/me/tags`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tags }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Failed to save'); return; }
      toast.success('Bundle Tags updated');
      setEditing(false);
      if (onSaved) onSaved();
    } catch {
      toast.error('Network error');
    } finally { setSaving(false); }
  };

  return (
    <Card className="mb-6 bg-white border-bundle-cream-dark" data-testid="bundle-tags-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Star className="w-5 h-5 text-bundle-gold" /> Bundle Tags
        </CardTitle>
        {!editing ? (
          <Button data-testid="edit-tags-btn" variant="outline" size="sm" onClick={() => setEditing(true)} className="text-bundle-green border-bundle-green">
            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setTags(initial || []); }} className="text-gray-500">Cancel</Button>
            <Button data-testid="save-tags-btn" size="sm" onClick={save} disabled={saving} className="btn-bundle">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {!editing ? (
          tags.length > 0 ? (
            <BundleTagsManager value={tags} mode="display" />
          ) : (
            <p className="text-sm text-gray-500">
              Add tags to help fellow Bundle members find you for networking, traveling, and golf — Veterans, fraternities, professions, location, and more.
            </p>
          )
        ) : (
          <BundleTagsManager value={tags} onChange={setTags} maxTags={30} />
        )}
      </CardContent>
    </Card>
  );
}

